import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";

import { faTrashCan } from "@fortawesome/pro-regular-svg-icons";

import { JobTitleScreeningItem, JobTitleVerdict } from "@signalist/shared-types";

import { Checkbox } from "@/components/Checkbox/Checkbox";
import { Icon } from "@/components/Icon/Icon";
import { Tooltip } from "@/components/Tooltip/Tooltip";
import { classNames } from "@/utils/classNames";

interface JobTitleRowProps {
	row: JobTitleScreeningItem;
	isSelected: boolean;
	/** The other column has an active selection — this row is not interactive. */
	isLocked: boolean;
	/** Label for the single-row move button ("Exclude" on the whitelist, "Whitelist" on excluded). */
	moveLabel: string;
	onToggleSelect: () => void;
	onMove: () => void;
	onDelete: () => void;
}

/** Persona attribution shown on whitelisted rows; hover reveals the AI reasoning. */
const PersonaAttribution: FunctionComponent<{ row: JobTitleScreeningItem }> = ({ row }) => {
	const { t } = useTranslation();

	if (!row.personaCategory) {
		return (
			<span className="text-xs text-text-meta" data-test="job-title-persona-manual">
				{t("leadScoring.jobTitles.manualLabel")}
			</span>
		);
	}

	const chip = (
		<span
			className="rounded-md bg-blue-action-200 px-2 py-0.5 text-xs font-semibold text-brand-light"
			data-test="job-title-persona-chip"
		>
			{row.personaCategory}
		</span>
	);

	return row.reasoning ? (
		<Tooltip content={row.reasoning} panel variant="dark">
			<span className="cursor-help">{chip}</span>
		</Tooltip>
	) : (
		chip
	);
};

export const JobTitleRow: FunctionComponent<JobTitleRowProps> = ({
	row,
	isSelected,
	isLocked,
	moveLabel,
	onToggleSelect,
	onMove,
	onDelete,
}) => {
	const { t } = useTranslation();
	const isWhitelisted = row.verdict === JobTitleVerdict.WHITELISTED;

	return (
		<div
			className={classNames(
				"group flex items-center gap-3 rounded-lg border border-transparent px-2 py-1.5 hover:border-gray-200 hover:bg-gray-50",
				isLocked && "opacity-40"
			)}
			data-test="job-title-row"
		>
			{/* The shared Checkbox wrapper is w-full; pin it so it can't collapse the title span. */}
			<div className="w-5 flex-shrink-0">
				<Checkbox
					isChecked={isSelected}
					isDisabled={isLocked}
					testId={`job-title-select-${row.id}`}
					onChange={() => !isLocked && onToggleSelect()}
				/>
			</div>
			<span className="min-w-0 flex-1 truncate text-sm text-text" title={row.title}>
				{row.title}
			</span>
			{isWhitelisted && <PersonaAttribution row={row} />}
			<div className="flex items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100">
				<button
					className="cursor-pointer rounded-md border border-gray-200 px-2 py-0.5 text-xs font-semibold text-text-meta hover:border-brand-light hover:text-brand-light disabled:cursor-not-allowed"
					data-test={`job-title-move-${row.id}`}
					disabled={isLocked}
					type="button"
					onClick={onMove}
				>
					{moveLabel}
				</button>
				<button
					aria-label={t("leadScoring.jobTitles.remove")}
					className="flex h-6 w-6 cursor-pointer items-center justify-center rounded-md text-text-meta hover:bg-status-error-light hover:text-status-error disabled:cursor-not-allowed"
					data-test={`job-title-delete-${row.id}`}
					disabled={isLocked}
					type="button"
					onClick={onDelete}
				>
					<Icon className="h-3.5 w-3.5" icon={faTrashCan} />
				</button>
			</div>
		</div>
	);
};
